import React, { useCallback, useEffect, useRef, useState } from 'react';
import AcademyHome from './AcademyHome';
import LessonViewer from './LessonViewer';
import Lesson1InteractiveVideo from './Lesson1InteractiveVideo';
import lesson5Data from '../../data/lessons/lesson5.json';
import { fetchLessonWithStepsBySlug } from '../../lib/supabaseLesson';
import { fetchBeginnerModules } from '../../lib/supabaseContent';
import { fetchUserLessonProgress } from '../../lib/academyOverview';
import {
  applyMyIqPointsDelta,
  computeMastersPathFromEloPoints,
  fetchMyLessonReward,
  fetchMyProfile
} from '../../lib/supabaseProfile';
import { canAccessLesson } from '../../lib/supabaseProgress';
import {
  fetchLichessPuzzleLessonById,
  fetchPuzzleLessonById,
  fetchPuzzlesByTheme,
  markPuzzleSolved
} from '../../lib/supabasePuzzles';
import RewardModal from './RewardModal';

function Academy({
  language,
  user,
  profile,
  initialArea = 'academy',
  initialMindhubArticleSlug = '',
  onRequestLogin,
  onOpenAccount,
  onProfileUpdated,
  onAreaChange,
  onMindhubArticleChange
}) {
  const [currentView, setCurrentView] = useState('home'); // 'home' or 'lesson'
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentLessonSlug, setCurrentLessonSlug] = useState('');
  const [trackLessonProgress, setTrackLessonProgress] = useState(true);
  const [isLoadingLesson, setIsLoadingLesson] = useState(false);
  const [lessonError, setLessonError] = useState('');
  const [initialSegment, setInitialSegment] = useState(0);
  const [currentPuzzleDiagramNo, setCurrentPuzzleDiagramNo] = useState(1);
  const [currentPuzzleFilter, setCurrentPuzzleFilter] = useState(null);
  const [focusLessonSlug, setFocusLessonSlug] = useState('');
  const [focusNonce, setFocusNonce] = useState(0);
  const [rewardModal, setRewardModal] = useState(null);
  const [pendingNavigationAfterReward, setPendingNavigationAfterReward] = useState(null);
  const [gateModal, setGateModal] = useState(null);
  const [sessionSolvedPuzzleIds, setSessionSolvedPuzzleIds] = useState([]);
  const [homeArea, setHomeArea] = useState(initialArea || 'academy');
  const puzzleDiagramNoRef = useRef(1);
  const puzzleFilterRef = useRef(null);
  const puzzleHeaderStatsRef = useRef(null);
  const toNullableNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const mapLessonErrorMessage = useCallback((err) => {
    const raw = String(err?.message || err || '').toLowerCase();
    if (!raw) {
      return language === 'ro'
        ? 'Lecția nu poate fi încărcată momentan. Te rugăm să încerci din nou.'
        : 'The lesson cannot be loaded right now. Please try again.';
    }
    if (raw.includes('lesson not found') || raw.includes('not found')) {
      return language === 'ro'
        ? 'Lecția nu este disponibilă momentan.'
        : 'This lesson is not available right now.';
    }
    if (raw.includes('network') || raw.includes('failed to fetch') || raw.includes('timeout')) {
      return language === 'ro'
        ? 'Conexiunea este instabilă. Încearcă din nou.'
        : 'Connection is unstable. Please try again.';
    }
    if (raw.includes('unauthorized') || raw.includes('invalid jwt') || raw.includes('forbidden')) {
      return language === 'ro'
        ? 'Sesiunea a expirat. Reautentifică-te pentru a continua.'
        : 'Your session has expired. Please sign in again to continue.';
    }
    return language === 'ro'
      ? 'Nu am putut încărca lecția acum.'
      : 'We could not load this lesson right now.';
  }, [language]);

  const mapGateBlockersUi = useCallback((blockers) => {
    const list = Array.isArray(blockers) ? blockers : [];
    const lines = [];
    let ctaLessonSlug = '';
    let ctaLabel = '';

    list.forEach((b) => {
      const code = String(b?.code || '').trim().toUpperCase();
      if (code === 'MISSING_REQUIRED_MODULE_COMPLETION') {
        const missing = Number(b?.missing_count || 0);
        lines.push(
          language === 'ro'
            ? `• Finalizează Modulul 1 (${missing} lecții rămase).`
            : `• Complete Module 1 (${missing} lessons remaining).`
        );
        if (!ctaLessonSlug) {
          ctaLessonSlug = 'b-m1-l1';
          ctaLabel = language === 'ro' ? 'Continuă Modulul 1' : 'Continue Module 1';
        }
        return;
      }
      if (code === 'RECAP_NOT_COMPLETED') {
        lines.push(
          language === 'ro'
            ? '• Completează recapitularea Modulului 1.'
            : '• Complete Module 1 recap.'
        );
        ctaLessonSlug = 'b-m1-recap';
        ctaLabel = language === 'ro' ? 'Mergi la recap' : 'Go to recap';
        return;
      }
      if (code === 'STANDARD_EXAM_NOT_PASSED') {
        lines.push(
          language === 'ro'
            ? '• Promovează examenul standard al Modulului 1.'
            : '• Pass Module 1 standard exam.'
        );
        if (!ctaLessonSlug || ctaLessonSlug !== 'b-m1-recap') {
          ctaLessonSlug = 'b-m1-exam-standard';
          ctaLabel = language === 'ro' ? 'Mergi la examen' : 'Go to exam';
        }
        return;
      }
      if (code === 'CAPTAIN_EXAM_NOT_PASSED') {
        lines.push(
          language === 'ro'
            ? '• Promovează examenul Captain.'
            : '• Pass the Captain exam.'
        );
        return;
      }
      if (code === 'MISSING_LESSON_PREREQUISITES') {
        const missing = Number(b?.missing_count || 0);
        lines.push(
          language === 'ro'
            ? `• Mai ai prerechizite nefinalizate (${missing}).`
            : `• You still have incomplete prerequisites (${missing}).`
        );
        return;
      }
      lines.push(
        language === 'ro'
          ? '• Există condiții de acces neîndeplinite.'
          : '• There are unmet access conditions.'
      );
    });

    if (!lines.length) {
      lines.push(
        language === 'ro'
          ? '• Există condiții de acces neîndeplinite.'
          : '• There are unmet access conditions.'
      );
    }

    return {
      title: language === 'ro' ? 'Lecție blocată momentan' : 'Lesson currently locked',
      subtitle: language === 'ro'
        ? 'Pentru deblocare, completează următoarele cerințe:'
        : 'To unlock this lesson, complete the following requirements:',
      lines,
      ctaLessonSlug,
      ctaLabel
    };
  }, [language]);

  const handleGateModalGo = useCallback(() => {
    if (!gateModal?.ctaLessonSlug) return;
    setGateModal(null);
    setCurrentView('home');
    setCurrentLesson(null);
    setLessonError('');
    setIsLoadingLesson(false);
    setHomeArea('academy');
    setFocusLessonSlug(gateModal.ctaLessonSlug);
    setFocusNonce((n) => n + 1);
  }, [gateModal]);

  const handleGateModalClose = useCallback(() => {
    setGateModal(null);
  }, []);

  const handleStartLesson = async (lessonSlug, options = {}) => {
    // Always open lessons from the top of the page.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    setLessonError('');
    setIsLoadingLesson(true);
    setCurrentView('lesson');
    setCurrentLessonSlug(lessonSlug);
    setInitialSegment(0);
    setTrackLessonProgress(options?.trackProgress !== false);
    if (options?.sourceArea) {
      setHomeArea(String(options.sourceArea));
    }
    setSessionSolvedPuzzleIds([]);
    if (!String(lessonSlug || '').startsWith('db-puzzle-')) {
      setCurrentPuzzleFilter(null);
      setCurrentPuzzleDiagramNo(1);
    }

    try {
      const supabaseLesson = await fetchLessonWithStepsBySlug(lessonSlug, language);
      if (supabaseLesson) {
        if (user?.id && supabaseLesson.lessonDbId) {
          try {
            const gate = await canAccessLesson({
              userId: user.id,
              lessonId: supabaseLesson.lessonDbId
            });
            if (gate?.canAccess === false) {
              setGateModal(mapGateBlockersUi(gate.blockers));
              setCurrentView('home');
              setCurrentLesson(null);
              setCurrentLessonSlug('');
              return;
            }
          } catch {
            // If gate check fails, don't hard-block the user.
          }
        }
        // Resume from saved progress if available.
        if (user?.id && supabaseLesson.lessonDbId && (supabaseLesson.segments || []).length) {
          try {
            const progressMap = await fetchUserLessonProgress([supabaseLesson.lessonDbId]);
            const p = progressMap.get(supabaseLesson.lessonDbId);
            if (p?.completed) {
              // If lesson is already done, Start should restart from the first step.
              setInitialSegment(0);
            } else {
              const currentStep = Math.max(1, Number(p?.currentStep || 1));
              const segIndex = Math.min(
                (supabaseLesson.segments.length || 1) - 1,
                Math.max(0, currentStep - 1)
              );
              setInitialSegment(segIndex);
            }
          } catch {
            // ignore resume failures; start at 0
          }
        }
        setCurrentLesson(supabaseLesson);
        return;
      }

      if (lessonSlug === 'lesson-5') {
        setCurrentLesson(lesson5Data);
        return;
      }

      if (lessonSlug.startsWith('db-puzzle-')) {
        const puzzleCatalogId = Number(String(lessonSlug).replace('db-puzzle-', ''));
        if (Number.isFinite(puzzleCatalogId) && puzzleCatalogId > 0) {
          const minRating = toNullableNumber(options?.minRating);
          const maxRating = toNullableNumber(options?.maxRating);
          const difficultyId = String(options?.puzzleDifficultyId || '').trim();
          const themeId = String(options?.puzzleTheme || '').trim().toLowerCase();
          const puzzleFilter = { difficultyId, minRating, maxRating, themeId };
          setCurrentPuzzleFilter(puzzleFilter);
          const dbPuzzleLesson = await fetchPuzzleLessonById(puzzleCatalogId, language);
          if (dbPuzzleLesson) {
            const diagramNo = Math.max(1, Number(options?.puzzleDiagramNo || 1));
            setCurrentPuzzleDiagramNo(diagramNo);
            const titledPuzzleLesson = {
              ...dbPuzzleLesson,
              title: language === 'ro' ? `Diagrama #${diagramNo}` : `Diagram #${diagramNo}`,
              puzzleDiagramNo: diagramNo,
              puzzleFilter,
              puzzleHeaderStats: options?.puzzleHeaderStats || null,
              puzzleLastOutcome: null
            };
            setCurrentLesson(titledPuzzleLesson);
            return;
          }
        }
      }

      if (lessonSlug.startsWith('lichess-puzzle-')) {
        const sourcePuzzleId = String(lessonSlug).replace('lichess-puzzle-', '').trim();
        if (sourcePuzzleId) {
          const lichessLesson = await fetchLichessPuzzleLessonById(sourcePuzzleId, language);
          if (lichessLesson) {
            const diagramNo = Math.max(1, Number(options?.puzzleDiagramNo || 1));
            setCurrentPuzzleDiagramNo(diagramNo);
            setCurrentLesson({
              ...lichessLesson,
              title: language === 'ro' ? `Diagrama #${diagramNo}` : `Diagram #${diagramNo}`,
              puzzleDiagramNo: diagramNo,
              puzzleFilter: null,
              puzzleHeaderStats: options?.puzzleHeaderStats || null,
              puzzleLastOutcome: null
            });
            return;
          }
        }
      }

      throw new Error('Lesson not found');
    } catch (error) {
      setLessonError(mapLessonErrorMessage(error));
      setCurrentLesson(null);
    } finally {
      setIsLoadingLesson(false);
    }
  };

  useEffect(() => {
    puzzleDiagramNoRef.current = currentPuzzleDiagramNo;
  }, [currentPuzzleDiagramNo]);

  useEffect(() => {
    puzzleFilterRef.current = currentPuzzleFilter;
  }, [currentPuzzleFilter]);

  useEffect(() => {
    puzzleHeaderStatsRef.current = currentLesson?.puzzleHeaderStats || null;
  }, [currentLesson?.puzzleHeaderStats]);

  useEffect(() => {
    let alive = true;

    async function reloadForLanguage() {
      if (currentView !== 'lesson') return;
      if (!currentLessonSlug) return;
      if (currentLessonSlug === 'lesson-5') return;

      setLessonError('');
      setIsLoadingLesson(true);
      setCurrentLesson(null);
      try {
        if (currentLessonSlug.startsWith('db-puzzle-')) {
          const puzzleCatalogId = Number(String(currentLessonSlug).replace('db-puzzle-', ''));
          if (Number.isFinite(puzzleCatalogId) && puzzleCatalogId > 0) {
            const dbPuzzleLesson = await fetchPuzzleLessonById(puzzleCatalogId, language);
            if (!alive) return;
            if (dbPuzzleLesson) {
              const diagramNo = Math.max(1, Number(puzzleDiagramNoRef.current || 1));
              setCurrentLesson({
                ...dbPuzzleLesson,
                title: language === 'ro' ? `Diagrama #${diagramNo}` : `Diagram #${diagramNo}`,
                puzzleDiagramNo: diagramNo,
                puzzleFilter: puzzleFilterRef.current || null,
                puzzleHeaderStats: puzzleHeaderStatsRef.current || null,
                puzzleLastOutcome: null
              });
              return;
            }
          }
        }
        if (currentLessonSlug.startsWith('lichess-puzzle-')) {
          const sourcePuzzleId = String(currentLessonSlug).replace('lichess-puzzle-', '').trim();
          if (sourcePuzzleId) {
            const lichessLesson = await fetchLichessPuzzleLessonById(sourcePuzzleId, language);
            if (!alive) return;
            if (lichessLesson) {
              const diagramNo = Math.max(1, Number(puzzleDiagramNoRef.current || 1));
              setCurrentLesson({
                ...lichessLesson,
                title: language === 'ro' ? `Diagrama #${diagramNo}` : `Diagram #${diagramNo}`,
                puzzleDiagramNo: diagramNo,
                puzzleFilter: puzzleFilterRef.current || null,
                puzzleHeaderStats: puzzleHeaderStatsRef.current || null,
                puzzleLastOutcome: null
              });
              return;
            }
          }
        }

        const supabaseLesson = await fetchLessonWithStepsBySlug(currentLessonSlug, language);
        if (!alive) return;
        if (supabaseLesson) setCurrentLesson(supabaseLesson);
      } catch (e) {
        if (!alive) return;
        setLessonError(mapLessonErrorMessage(e));
      } finally {
        if (!alive) return;
        setIsLoadingLesson(false);
      }
    }

    reloadForLanguage();
    return () => {
      alive = false;
    };
  }, [language, currentView, currentLessonSlug, mapLessonErrorMessage]);

  useEffect(() => {
    if (currentView !== 'lesson') return;
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentView, currentLessonSlug]);

  useEffect(() => {
    if (currentView !== 'home') return;
    if (!initialArea) return;
    setHomeArea(initialArea);
  }, [initialArea, currentView]);

  useEffect(() => {
    if (typeof onAreaChange === 'function') {
      onAreaChange(homeArea);
    }
  }, [homeArea, onAreaChange]);

  const handleCloseLesson = () => {
    setCurrentView('home');
    setCurrentLesson(null);
    setLessonError('');
    setIsLoadingLesson(false);
    setCurrentPuzzleDiagramNo(1);
    setCurrentPuzzleFilter(null);
    setSessionSolvedPuzzleIds([]);
  };

  const handleBackFromLesson = () => {
    const slug = String(currentLessonSlug || '').trim();
    const isAcademyLesson = slug && !slug.startsWith('db-puzzle-');
    handleCloseLesson();
    if (isAcademyLesson) {
      setHomeArea('academy');
      setFocusLessonSlug(slug);
      setFocusNonce((n) => n + 1);
    }
  };

  const handleRewardModalContinue = async () => {
    const pending = pendingNavigationAfterReward;
    setRewardModal(null);
    setPendingNavigationAfterReward(null);

    if (!pending) return;

    try {
      if (pending.type === 'next-in-module' && pending.lessonId) {
        await handleStartLesson(pending.lessonId, { sourceArea: pending.sourceArea || homeArea || 'academy' });
        return;
      }

      if (pending.type === 'focus-next-global' && pending.lessonId) {
        handleCloseLesson();
        setFocusLessonSlug(pending.lessonId);
        setFocusNonce((n) => n + 1);
        return;
      }

      handleCloseLesson();
    } catch {
      handleCloseLesson();
    }
  };

  const handleLessonComplete = async ({
    lessonSlug,
    lessonDbId,
    lessonTitle,
    puzzleCatalogId,
    puzzleResult,
    puzzleScoredAlready,
    puzzleAward,
    playedMoveUci,
    tries,
    usedSolution,
    solveMs,
    continueTraining
  } = {}) => {
    const isPuzzleLesson =
      Boolean(puzzleCatalogId) ||
      String(lessonSlug || '').startsWith('db-puzzle-') ||
      String(currentLessonSlug || '').startsWith('db-puzzle-') ||
      String(currentLesson?.lessonType || '').toLowerCase() === 'puzzle';

    let awardFromDb = null;
    let computedOutcome = null;
    const normalizedPuzzleResult = String(puzzleResult || '').trim().toLowerCase();
    try {
      if (puzzleCatalogId) {
        if (puzzleScoredAlready && puzzleAward) {
          awardFromDb = {
            result: String(puzzleAward?.result || normalizedPuzzleResult || '').trim().toLowerCase(),
            puzzle_points_delta: Number(puzzleAward?.pointsDelta || puzzleAward?.puzzle_points_delta || 0),
            puzzle_rating_delta: Number(puzzleAward?.ratingDelta || puzzleAward?.puzzle_rating_delta || 0)
          };
        } else {
          awardFromDb = await markPuzzleSolved({
            puzzleCatalogId,
            result: normalizedPuzzleResult || '',
            playedMoveUci: playedMoveUci || '',
            tries: Number(tries || 1),
            usedSolution: Boolean(usedSolution),
            solveMs: Number.isFinite(Number(solveMs)) ? Number(solveMs) : null
          });
        }
        if (awardFromDb && typeof onProfileUpdated === 'function') {
          try {
            if (!puzzleScoredAlready) {
              const iqDelta = Number(awardFromDb?.puzzle_points_delta || 0);
              if (iqDelta !== 0) {
                await applyMyIqPointsDelta(iqDelta);
              }
            }
            await onProfileUpdated();
          } catch {
            // ignore profile refresh errors in puzzle flow
          }
        }
        if (awardFromDb) {
          const normalized = String(awardFromDb?.result || normalizedPuzzleResult || '').trim().toLowerCase();
          computedOutcome = {
            result: normalized,
            pointsDelta: Number(awardFromDb?.puzzle_points_delta || 0),
            ratingDelta: Number(awardFromDb?.puzzle_rating_delta || 0)
          };
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Academy] Puzzle scoring RPC failed', String(e?.message || e));
    }

    if (isPuzzleLesson && !computedOutcome && normalizedPuzzleResult) {
      computedOutcome = {
        result: normalizedPuzzleResult,
        pointsDelta: Number(awardFromDb?.puzzle_points_delta || 0),
        ratingDelta: Number(awardFromDb?.puzzle_rating_delta || 0)
      };
    }

    // Keep a local "solved in this session" set to avoid repeats even if DB read is slightly stale.
    if (isPuzzleLesson) {
      const currentPuzzleId = Number(
        puzzleCatalogId ||
        String(lessonSlug || '').replace('db-puzzle-', '') ||
        String(currentLessonSlug || '').replace('db-puzzle-', '')
      );
      const outcomeKey = String(computedOutcome?.result || normalizedPuzzleResult || '').toLowerCase();
      if (Number.isFinite(currentPuzzleId) && currentPuzzleId > 0) {
        if (outcomeKey === 'success' || outcomeKey === 'success_hint' || outcomeKey === 'already_solved') {
          setSessionSolvedPuzzleIds((prev) => (prev.includes(currentPuzzleId) ? prev : [...prev, currentPuzzleId]));
        }
      }
    }

    if (isPuzzleLesson) {
      const shouldContinue =
        continueTraining === undefined
          ? true
          : Boolean(continueTraining);
      if (!shouldContinue) {
        handleCloseLesson();
        return;
      }
      try {
        const minRating =
          toNullableNumber(currentLesson?.puzzleFilter?.minRating) ??
          toNullableNumber(currentPuzzleFilter?.minRating);
        const maxRating =
          toNullableNumber(currentLesson?.puzzleFilter?.maxRating) ??
          toNullableNumber(currentPuzzleFilter?.maxRating);
        const difficultyId =
          String(currentLesson?.puzzleFilter?.difficultyId || currentPuzzleFilter?.difficultyId || '').trim();
        const themeId =
          String(currentLesson?.puzzleFilter?.themeId || currentPuzzleFilter?.themeId || 'matein1')
            .trim()
            .toLowerCase();

        const rows = await fetchPuzzlesByTheme({
          theme: themeId,
          limit: 120,
          userId: user?.id || '',
          minRating,
          maxRating
        });
        const currentId = Number(
          puzzleCatalogId ||
          String(lessonSlug || '').replace('db-puzzle-', '') ||
          String(currentLessonSlug || '').replace('db-puzzle-', '')
        );
        const nextRow = (rows || []).find((r) => {
          const id = Number(r?.id);
          if (!Number.isFinite(id) || id <= 0) return false;
          if (id === currentId) return false;
          if (sessionSolvedPuzzleIds.includes(id)) return false;
          return true;
        }) || null;

        if (nextRow?.id) {
          const nextPuzzleId = Number(nextRow.id);
          const nextLesson = await fetchPuzzleLessonById(nextPuzzleId, language);
          if (nextLesson) {
            const nextDiagramNo = Math.max(1, Number(currentPuzzleDiagramNo || 1) + 1);
            setCurrentPuzzleDiagramNo(nextDiagramNo);
            setCurrentLessonSlug(`db-puzzle-${nextPuzzleId}`);
            setCurrentLesson({
              ...nextLesson,
              title: language === 'ro' ? `Diagrama #${nextDiagramNo}` : `Diagram #${nextDiagramNo}`,
              puzzleDiagramNo: nextDiagramNo,
              puzzleLastOutcome: null,
              puzzleHeaderStats: {
                ...(currentLesson?.puzzleHeaderStats || {}),
                puzzlePoints: Math.max(
                  0,
                  Number(currentLesson?.puzzleHeaderStats?.puzzlePoints || 0) +
                    Number(computedOutcome?.pointsDelta || 0)
                ),
                puzzleRating: Math.max(
                  500,
                  Number(currentLesson?.puzzleHeaderStats?.puzzleRating || 500) +
                    Number(computedOutcome?.ratingDelta || 0)
                ),
                iqPoints: Math.max(
                  0,
                  Number(currentLesson?.puzzleHeaderStats?.iqPoints ?? profile?.eloPoints ?? 0) +
                    Number(computedOutcome?.pointsDelta || 0)
                )
              },
              puzzleFilter: {
                difficultyId,
                themeId,
                minRating,
                maxRating
              }
            });
            setCurrentPuzzleFilter({
              difficultyId,
              themeId,
              minRating,
              maxRating
            });
            setCurrentView('lesson');
            setLessonError('');
            setIsLoadingLesson(false);
            return;
          }
        }
      } catch {
        // ignore and fall through
      }

      // No next puzzle found: keep learner in puzzle screen and let them exit manually.
      return;
    }

    const completedSlug = currentLessonSlug;

    // Refresh profile (IQ Points / level) after completion.
    // The award trigger runs in the same transaction as progress completion, but we still
    // wait a tick so the UI is never ahead of PostgREST.
    try {
      if (typeof onProfileUpdated === 'function') {
        await new Promise((r) => window.setTimeout(r, 250));
        await onProfileUpdated();
      }
    } catch {
      // ignore
    }

    let hasRewardModal = false;
    // Fetch reward info and show a completion celebration (only if it was freshly awarded).
    try {
      if (user?.id && lessonDbId) {
        // Small retry loop: DB trigger should be immediate, but PostgREST can lag.
        let reward = null;
        for (let i = 0; i < 5; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          reward = await fetchMyLessonReward(lessonDbId);
          if (reward?.awarded_at) break;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => window.setTimeout(r, 180));
        }

        if (reward?.awarded_at) {
          const awardedAtMs = new Date(reward.awarded_at).getTime();
          const fresh = Math.abs(Date.now() - awardedAtMs) < 5 * 60 * 1000;

          const p = await fetchMyProfile();
          const eloPoints = Number(p?.eloPoints ?? p?.elo_points ?? 0);
          const mp = computeMastersPathFromEloPoints(eloPoints);

          const rankKey = String(p?.computedRankKey || mp.rankKey || 'explorer');
          const rankLabel =
            language === 'ro'
              ? ({
                  explorer: 'Explorator',
                  apprentice: 'Ucenic',
                  strategist: 'Strateg',
                  champion: 'Campion',
                  master: 'Maestru',
                  legend: 'Legendă'
                }[rankKey] || 'Explorator')
              : ({
                  explorer: 'Explorer',
                  apprentice: 'Apprentice',
                  strategist: 'Strategist',
                  champion: 'Champion',
                  master: 'Master',
                  legend: 'Legend'
                }[rankKey] || 'Explorer');

          // Show celebration modal even if the reward was earned earlier,
          // but keep the "earned +X" language only for fresh awards.
          setRewardModal({
            nickname: String(p?.nickname || p?.display_name || '').trim(),
            lessonTitle: lessonTitle || null,
            pointsAwarded: Number(reward.points_awarded || 0),
            multiplier: Number(reward.multiplier || 1),
            totalPoints: eloPoints,
            level: mp.level,
            rankLabel,
            nextPointsToNext: mp.pointsToNext,
            progressPct: Math.round(Number(mp.progressToNext || 0) * 100),
            isFresh: Boolean(fresh)
          });
          hasRewardModal = true;
        }
      }
    } catch {
      // ignore reward modal failures
    }

    // Open the next lesson in the same module automatically.
    // If none is available, focus next lesson in home as fallback.
    try {
      const modules = await fetchBeginnerModules(language);
      const list = modules || [];
      let nextInSameModule = null;
      let nextGlobal = null;

      for (let mi = 0; mi < list.length; mi += 1) {
        const moduleLessons = list[mi]?.lessons || [];
        const idx = moduleLessons.findIndex((l) => l.id === completedSlug);
        if (idx >= 0) {
          // Same module
          for (let j = idx + 1; j < moduleLessons.length; j += 1) {
            const cand = moduleLessons[j];
            if (!cand) continue;
            if (cand.comingSoon) continue;
            if (cand.available === false) continue;
            if (cand.guestLocked) continue;
            if (cand.planLocked) continue;
            nextInSameModule = cand;
            break;
          }
          break;
        }
      }

      if (hasRewardModal) {
        if (nextInSameModule?.id) {
          setPendingNavigationAfterReward({
            type: 'next-in-module',
            lessonId: nextInSameModule.id,
            sourceArea: homeArea || 'academy'
          });
          return;
        }

        const allLessons = list.flatMap((m) => m.lessons || []);
        const idxAll = allLessons.findIndex((l) => l.id === completedSlug);
        for (let k = idxAll + 1; k < allLessons.length; k += 1) {
          const cand = allLessons[k];
          if (!cand) continue;
          if (cand.comingSoon) continue;
          if (cand.available === false) continue;
          if (cand.guestLocked) continue;
          if (cand.planLocked) continue;
          nextGlobal = cand;
          break;
        }

        if (nextGlobal?.id) {
          setPendingNavigationAfterReward({
            type: 'focus-next-global',
            lessonId: nextGlobal.id
          });
        } else {
          setPendingNavigationAfterReward({ type: 'close' });
        }
        return;
      }

      if (nextInSameModule?.id) {
        await handleStartLesson(nextInSameModule.id, { sourceArea: homeArea || 'academy' });
        return;
      }

      const allLessons = list.flatMap((m) => m.lessons || []);
      const idxAll = allLessons.findIndex((l) => l.id === completedSlug);
      for (let k = idxAll + 1; k < allLessons.length; k += 1) {
        const cand = allLessons[k];
        if (!cand) continue;
        if (cand.comingSoon) continue;
        if (cand.available === false) continue;
        if (cand.guestLocked) continue;
        if (cand.planLocked) continue;
        nextGlobal = cand;
        break;
      }

      handleCloseLesson();

      if (nextGlobal?.id) {
        setFocusLessonSlug(nextGlobal.id);
        setFocusNonce((n) => n + 1);
      }
    } catch {
      handleCloseLesson();
      // ignore
    }
  };

  const handlePuzzleScored = async ({
    puzzleCatalogId,
    puzzleResult,
    playedMoveUci,
    tries,
    usedSolution,
    solveMs
  } = {}) => {
    if (!puzzleCatalogId) return null;
    const normalizedPuzzleResult = String(puzzleResult || '').trim().toLowerCase();
    try {
      const award = await markPuzzleSolved({
        puzzleCatalogId,
        result: normalizedPuzzleResult || '',
        playedMoveUci: playedMoveUci || '',
        tries: Number(tries || 1),
        usedSolution: Boolean(usedSolution),
        solveMs: Number.isFinite(Number(solveMs)) ? Number(solveMs) : null
      });
      if (!award) return null;

      const computedOutcome = {
        result: String(award?.result || normalizedPuzzleResult || '').trim().toLowerCase(),
        pointsDelta: Number(award?.puzzle_points_delta || 0),
        ratingDelta: Number(award?.puzzle_rating_delta || 0)
      };
      if (typeof onProfileUpdated === 'function') {
        try {
          const iqDelta = Number(award?.puzzle_points_delta || 0);
          if (iqDelta !== 0) {
            await applyMyIqPointsDelta(iqDelta);
          }
          await onProfileUpdated();
        } catch {
          // ignore profile refresh errors
        }
      }
      return computedOutcome;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Academy] Puzzle immediate scoring RPC failed', String(e?.message || e));
      return null;
    }
  };

  return (
    <div className="academy-container">
      {currentView === 'home' ? (
        <AcademyHome 
          language={language} 
          user={user}
          profile={profile}
          initialArea={homeArea}
          onAreaChange={setHomeArea}
          initialMindhubArticleSlug={initialMindhubArticleSlug}
          onMindhubArticleChange={onMindhubArticleChange}
          onRequestLogin={onRequestLogin}
          onOpenAccount={onOpenAccount}
          onStartLesson={handleStartLesson}
          focusLessonSlug={focusLessonSlug}
          focusNonce={focusNonce}
        />
      ) : (
        currentLessonSlug === 'b-m1-l1' ? (
          <Lesson1InteractiveVideo
            lessonData={currentLesson}
            language={language}
            user={user}
            onClose={handleBackFromLesson}
            onComplete={handleLessonComplete}
          />
        ) : (
          <LessonViewer
            lessonData={currentLesson}
            isLoading={isLoadingLesson}
            error={lessonError}
            language={language}
            user={user}
            trackProgress={trackLessonProgress}
            onRequestLogin={onRequestLogin}
            onClose={handleBackFromLesson}
            onPuzzleScored={handlePuzzleScored}
            onComplete={handleLessonComplete}
            initialSegment={initialSegment}
          />
        )
      )}
      <RewardModal
        language={language}
        isOpen={Boolean(rewardModal)}
        onClose={handleRewardModalContinue}
        nickname={rewardModal?.nickname || (user?.email ? String(user.email).split('@')[0] : '')}
        lessonTitle={rewardModal?.lessonTitle}
        pointsAwarded={rewardModal?.pointsAwarded}
        multiplier={rewardModal?.multiplier}
        rankLabel={rewardModal?.rankLabel}
        level={rewardModal?.level}
        totalPoints={rewardModal?.totalPoints}
        nextPointsToNext={rewardModal?.nextPointsToNext}
        progressPct={rewardModal?.progressPct}
      />
      {gateModal && (
        <div className="cm-modal-overlay" role="dialog" aria-modal="true">
          <div className="cm-modal-card">
            <h3>{gateModal.title}</h3>
            <p>{gateModal.subtitle}</p>
            <div style={{ margin: '-0.2rem 0 0.9rem', color: '#334155', lineHeight: 1.6 }}>
              {gateModal.lines.map((line, idx) => (
                <div key={`gate-line-${idx}`}>{line}</div>
              ))}
            </div>
            <div className="cm-modal-actions">
              <button className="btn-card" onClick={handleGateModalClose}>
                {language === 'ro' ? 'Am înțeles' : 'Got it'}
              </button>
              {gateModal.ctaLessonSlug && (
                <button className="btn-start-lesson mini continue" onClick={handleGateModalGo}>
                  {gateModal.ctaLabel || (language === 'ro' ? 'Mergi la lecția necesară' : 'Go to required lesson')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Academy;
